import type { Container } from 'inversify';
import Logger from '@framework/sentry/Logger';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { IDictionaryMarket } from '@/creditflowresearch/domain/vo/IDictionary';
import { IDictionaryMarketSegment } from '@/creditflowresearch/domain/vo/IDictionary';
import DictionaryRepository from '@/creditflowresearch/infrastructure/repository/DictionaryRepository';

export interface IDomainDictionaries {
  products?: IProduct[],
  regions?: IRegion[],
  markets?: IDictionaryMarket[],
  marketSegments?: IDictionaryMarketSegment[],
}

type DomainInitializer = (container: Container, data?: IDomainDictionaries) => Promise<void>;

class DomainRegistry {
  private static initializers: DomainInitializer[] = [];

  static register(initializer: DomainInitializer): void {
    this.initializers.push(initializer);
  }

  private static async loadDomainData(container: Container): Promise<IDomainDictionaries> {
    const dictionaryRepository = container.get<DictionaryRepository>(DictionaryRepository);

    const [products, regions, markets, marketSegments] = await Promise.all([
      dictionaryRepository.getProducts(),
      dictionaryRepository.getRegions(),
      dictionaryRepository.getMarkets(),
      dictionaryRepository.getMarketSegments(),
    ]);

    return {
      products,
      regions,
      markets,
      marketSegments,
    };
  }

  private static async initializeDomains(container: Container, data: IDomainDictionaries): Promise<void> {
    await Promise.all(
      this.initializers.map((init) => init(container, data)),
    );
  }

  static async initialize(container: Container, data?: IDomainDictionaries): Promise<IDomainDictionaries> {
    try {
      const domainDictionaries = data || await this.loadDomainData(container);
      await this.initializeDomains(container, domainDictionaries);
      return domainDictionaries;
    } catch (error) {
      Logger.error({ message: '[DomainRegistry] Initialization failed:', error });
      // TODO: @ihor-tanchyk We need to decide whether we return error or object below
      return { products: [], regions: [], markets: [], marketSegments: [] };
    }
  }
}

export default DomainRegistry;
